/**
 * Thin wrapper around AsyncStorage that catches disk-full errors (POSIX
 * code 28 / ENOSPC) and downgrades them to handled Sentry warnings instead
 * of unhandled promise rejections.
 *
 * When a disk-full error is detected the low-storage alert is shown so the
 * user knows to free space.
 */
import AsyncStorage from "@react-native-async-storage/async-storage";
import { captureException } from "@utils/captureException";
import { isDiskFullError, showLowStorageAlert } from "@utils/diskSpace";

function handleDiskFullError(err: unknown, operation: string): void {
  captureException(err, {
    channel: "warning",
    area: "storage",
    operation,
    tags: { errorType: "disk-full" },
  });

  // Show the low-storage alert so the user is informed.
  showLowStorageAlert();
}

export const safeAsyncStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      return await AsyncStorage.getItem(key);
    } catch (err) {
      if (isDiskFullError(err)) {
        handleDiskFullError(err, `async-storage-get:${key}`);
        return null;
      }
      throw err;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    try {
      await AsyncStorage.setItem(key, value);
    } catch (err) {
      if (isDiskFullError(err)) {
        handleDiskFullError(err, `async-storage-set:${key}`);
        return;
      }
      throw err;
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(key);
    } catch (err) {
      if (isDiskFullError(err)) {
        handleDiskFullError(err, `async-storage-remove:${key}`);
        return;
      }
      throw err;
    }
  },
};
